/**
 * Live FX rates from FinMind TaiwanExchangeRate dataset.
 * Docs: https://finmind.github.io/v3/tutor/ExchangeRate/
 *
 * Strategy:
 *   - On app boot, refresh rates for every supported currency in parallel
 *     (non-blocking). Results overwrite the static `FX_RATES` map in
 *     `./fx.ts`. If a fetch fails, the static fallback stays.
 *   - Cached in localStorage with `MAX_AGE_MS` (12h). Cache hits skip the
 *     network entirely so cold-load is fast and offline-friendly.
 *   - Rate selection: `spot_sell` is the spot-market sell rate, the closest
 *     to "what 1 unit of CCY costs in TWD today". When the API returns 0
 *     (KRW spot is sometimes 0), fall back to `cash_sell`.
 *
 * Sharp edges:
 *   - The endpoint is anonymous and unauthenticated. No tokens are sent.
 *   - Result is per-currency; we issue N requests in parallel — bounded
 *     by `CURRENCIES_TO_FETCH.length` (~8). Each request is small (~1 KB).
 *   - Failures are silent (swallowed via .catch) so the app never crashes
 *     on a transient FinMind outage; the static fallback ensures conversion
 *     keeps working.
 */

import { FX_RATES, type FxRate } from './fx';

const ENDPOINT = 'https://api.finmindtrade.com/api/v4/data';
const DATASET = 'TaiwanExchangeRate';
const STORAGE_KEY = 'mochia.fxCache.v1';
const MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12 hours

/**
 * Currencies to refresh from FinMind. TWD itself is the pivot (rate = 1)
 * and is excluded — there's no "TWD/TWD" exchange rate.
 */
const CURRENCIES_TO_FETCH = ['USD', 'JPY', 'EUR', 'KRW', 'CNY', 'HKD', 'THB', 'SGD'] as const;

interface FinMindRow {
  date: string;
  currency: string;
  cash_buy: number;
  cash_sell: number;
  spot_buy: number;
  spot_sell: number;
}

interface FinMindResponse {
  msg: string;
  status: number;
  data: FinMindRow[];
}

interface CacheShape {
  fetchedAt: number;
  rates: Record<string, number>;
}

function readCache(): CacheShape | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheShape;
    if (typeof parsed.fetchedAt !== 'number' || !parsed.rates) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(rates: Record<string, number>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const payload: CacheShape = { fetchedAt: Date.now(), rates };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota exceeded / private mode — non-fatal */
  }
}

function applyRates(rates: Record<string, number>): void {
  // FX_RATES is exported as Readonly<Record> — we mutate via the underlying
  // object reference. The "live" override semantics is intentional: bump
  // the in-memory map so every subsequent convertMinor call uses fresh
  // rates without code-level wiring everywhere.
  for (const [code, rate] of Object.entries(rates)) {
    if (!Number.isFinite(rate) || rate <= 0) continue;
    const upper = code.toUpperCase();
    const existing = (FX_RATES as Record<string, FxRate>)[upper];
    if (existing) existing.rate = rate;
    else (FX_RATES as Record<string, FxRate>)[upper] = { code: upper, rate };
  }
}

/**
 * Fetch the latest rate for one currency. We pull a 7-day window so we
 * always get a row even when today is a weekend / holiday and the bank
 * hasn't published yet.
 */
async function fetchOne(currency: string): Promise<number | null> {
  const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
  const url =
    `${ENDPOINT}?dataset=${DATASET}` +
    `&data_id=${encodeURIComponent(currency)}` +
    `&start_date=${start}`;
  let resp: Response;
  try {
    resp = await fetch(url);
  } catch {
    return null;
  }
  if (!resp.ok) return null;
  let body: FinMindResponse;
  try {
    body = (await resp.json()) as FinMindResponse;
  } catch {
    return null;
  }
  const rows = body.data ?? [];
  if (rows.length === 0) return null;
  // Pick the latest row (FinMind returns ascending by date).
  const latest = rows[rows.length - 1]!;
  // Prefer spot_sell; fall back to cash_sell when spot is 0 (e.g. KRW).
  const rate = latest.spot_sell > 0 ? latest.spot_sell : latest.cash_sell;
  return rate > 0 ? rate : null;
}

/**
 * Hydrate `FX_RATES` from the cache (synchronous, no network). Call this
 * before bootstrap so the first render uses cached live rates instead of
 * the static fallback. Safe to call multiple times.
 */
export function hydrateFromCache(): void {
  const cached = readCache();
  if (!cached) return;
  applyRates(cached.rates);
}

/**
 * Refresh rates from FinMind in the background. Returns a Promise so callers
 * can `await` if they want to, but typically fired-and-forgotten on boot.
 * No-op when the cache is fresher than `MAX_AGE_MS`.
 */
export async function refreshLiveRates(): Promise<void> {
  const cached = readCache();
  if (cached && Date.now() - cached.fetchedAt < MAX_AGE_MS) return;

  const results = await Promise.allSettled(
    CURRENCIES_TO_FETCH.map(async (c) => [c, await fetchOne(c)] as const),
  );

  const fresh: Record<string, number> = {};
  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    const [code, rate] = r.value;
    if (rate != null) fresh[code] = rate;
  }
  if (Object.keys(fresh).length === 0) return; // total failure — keep static fallback
  applyRates(fresh);
  writeCache(fresh);
}
